import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import FormWrapper from 'components/form-wrapper';
import Loader from 'components/loader';
import Steps from 'components/steps';
import BottomActions from 'features/bottom-actions';
import { STEEL_ROUTES } from 'shared/constants';
import useIsFormFilled from 'shared/hooks/useIsFormFilled';
import useProgressSteps from 'shared/hooks/useProgressSteps';
import useSessionStorage from 'shared/hooks/useSessionStorage';
import {
  SESSION_STORAGE_KEYS,
  removeFromSession,
} from 'shared/sessionStorageUtils';
import { LoadbearingStructure, ProgressStep } from 'shared/types';
import useGetSteelOrientations from '../api/useGetSteelOrientations';
import useGetSteelProfileShapes from '../api/useGetSteelProfileShapes';
import { CUSTOM_PROFILE } from '../constants';
import { SteelStructuralElementsValues } from '../types';
import Orientations from './components/orientations';
import ProfileShapes from './components/profile-shapes';
import { StyledForm } from './styles';
import { requiredFieldsValidationSchema, validationSchema } from './validation';

const emptyValues = {
  profileShape: undefined,
  orientation: undefined,
  profileType: undefined,
  profileSubtype: undefined,
  sectionFactor: undefined,
  sectionType: undefined,
};

const SteelStructuralElements = () => {
  const navigate = useNavigate();
  const [hasProtectedSides, setHasProtectedSides] = useState(false);
  const progressSteps = useProgressSteps(
    LoadbearingStructure.steel,
    hasProtectedSides
  );
  const { steelProfileShapes, isLoading: isShapesLoading } =
    useGetSteelProfileShapes();
  const { steelOrientations, isLoading: isOrientationsLoading } =
    useGetSteelOrientations();
  const [storedValues, setStoredValues] = useSessionStorage(
    SESSION_STORAGE_KEYS.steelStructuralElements
  );
  const initialValues = storedValues ?? emptyValues;
  const isLoading = isShapesLoading || isOrientationsLoading;

  const handleBackClick = () => {
    removeFromSession(SESSION_STORAGE_KEYS.product);
    removeFromSession(SESSION_STORAGE_KEYS.steelStructuralElements);
    navigate(STEEL_ROUTES.productSelect);
  };

  const handleSubmit = (values: SteelStructuralElementsValues) => {
    setStoredValues(values);
    navigate(
      hasProtectedSides ? STEEL_ROUTES.protectedSides : STEEL_ROUTES.environment
    );
  };

  return isLoading ? (
    <Loader />
  ) : (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => handleSubmit(values)}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ values, errors, touched, resetForm, setFieldValue }) => {
        const isFormFilled = useIsFormFilled(
          values,
          requiredFieldsValidationSchema
        );
        useEffect(() => {
          if (
            storedValues &&
            storedValues.profileShape !== values.profileShape
          ) {
            setStoredValues(null);
            resetForm({
              values: {
                ...emptyValues,
                profileShape: values.profileShape,
                orientation: values.orientation,
              },
            });
          } else {
            resetForm({
              values: {
                ...initialValues,
                profileShape: values.profileShape,
                orientation: values.orientation,
              },
            });
          }
          setHasProtectedSides(values.profileShape !== CUSTOM_PROFILE);
        }, [values.profileShape]);

        useEffect(() => {
          if (
            (values.profileType && !storedValues) ||
            (storedValues && storedValues.profileType !== values.profileType)
          ) {
            setFieldValue('profileSubtype', '');
          }
        }, [values.profileType]);

        return (
          <StyledForm>
            <FormWrapper>
              <Steps
                items={progressSteps}
                active={ProgressStep.structuralElements}
              />
              {steelProfileShapes && (
                <ProfileShapes values={values} shapes={steelProfileShapes}/>
              )}
              {steelOrientations && (
                <Orientations
                  values={values}
                  error={errors.orientation as string}
                  touched={!!touched.orientation}
                  orientations={steelOrientations}
                />
              )}
            </FormWrapper>
            <BottomActions
              onBackClick={handleBackClick}
              hasContinueBtn
              isContinueDisabled={!isFormFilled}
            />
          </StyledForm>
        );
      }}
    </Formik>
  );
};

export default SteelStructuralElements;
